import { Link } from 'react-router-dom';
import { useStaggeredAnimation } from '@/hooks/useScrollAnimation';

const services = [
  { title: 'Pediatría', desc: 'Cuidado integral para niños y adolescentes, desde el nacimiento.', icon: 'ri-bear-smile-line' },
  { title: 'Ginecología', desc: 'Salud de la mujer, control prenatal y prevención.', icon: 'ri-women-line' },
  { title: 'Cirugía General', desc: 'Procedimientos con técnicas modernas y seguimiento.', icon: 'ri-surgical-mask-line' },
  { title: 'Neurología', desc: 'Trastornos del sistema nervioso, migrañas y epilepsia.', icon: 'ri-brain-line' },
  { title: 'Cardiología', desc: 'Evaluación y manejo cardiovascular avanzado.', icon: 'ri-heart-pulse-line' },
];

export default function ServicesSection() {
  const { ref, isVisible, getDelay } = useStaggeredAnimation();

  return (
    <section className="py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-background-100 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header — split */}
        <div ref={ref} className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 md:mb-16">
          <div className={`max-w-xl reveal ${isVisible ? 'is-in' : ''}`}>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-primary-500 uppercase mb-4">
              <span className="w-8 h-px bg-primary-400" />
              Especialidades
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-950 leading-[1.08]">
              Servicios para toda <span className="italic text-primary-600">tu familia</span>
            </h2>
          </div>
          <p className={`lg:max-w-xs text-foreground-600 text-sm md:text-base leading-relaxed reveal ${isVisible ? 'is-in' : ''}`}>
            Más de 15 especialidades médicas con profesionales calificados. Consultorios abiertos las 24 horas.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Big featured card with image — spans 2 cols + 2 rows on desktop */}
          <div
            className={`md:col-span-2 md:row-span-2 group relative rounded-3xl overflow-hidden min-h-[340px] reveal-scale ${isVisible ? 'is-in' : ''}`}
            style={{ transitionDelay: getDelay(0) }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20professional%20medical%20consultation%20room%20interior%20with%20soft%20natural%20light%2C%20doctor%20and%20patient%20blurred%20in%20background%2C%20clean%20modern%20healthcare%20environment%2C%20warm%20cream%20and%20red%20tones%2C%20editorial%20photography%20with%20soft%20shadows&width=900&height=700&seq=svc-featured-vm&orientation=landscape"
              alt="Emergencias Vitalmedic 24 horas"
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1.2s] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/90 via-secondary-950/40 to-secondary-950/10" />
            <div className="relative h-full flex flex-col justify-end p-7 md:p-9">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-500 text-secondary-950 mb-5">
                <i className="ri-heart-pulse-line text-2xl" />
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-background-50 mb-2">
                Emergencias 24/7
              </h3>
              <p className="text-background-200 text-sm md:text-base leading-relaxed max-w-md mb-5">
                Atención inmediata para urgencias, traumatismos y condiciones críticas, todos los días del año.
              </p>
              <Link
                to="/servicios"
                className="inline-flex items-center gap-2 text-sm font-semibold text-accent-500 hover:text-accent-400 transition-colors w-fit cursor-pointer"
              >
                Conocer el servicio
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
          </div>

          {/* Small cards */}
          {services.map((service, i) => (
            <div
              key={service.title}
              className={`group bg-background-50 rounded-3xl p-6 md:p-7 border border-background-200 hover:border-primary-300 transition-all duration-500 reveal ${isVisible ? 'is-in' : ''}`}
              style={{ transitionDelay: getDelay(i + 1) }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
                <i className={`${service.icon} text-xl`} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1.5">
                {service.title}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {service.desc}
              </p>
            </div>
          ))}
        </div>

        <div className={`text-center mt-12 reveal ${isVisible ? 'is-in' : ''}`}>
          <Link
            to="/servicios"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-secondary-950 text-background-50 text-sm font-semibold hover:bg-secondary-900 transition-all whitespace-nowrap cursor-pointer"
          >
            Ver las 13 especialidades
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}